import { useCallback, useEffect, useState } from 'react';
import {
  warehouseApi,
  type WarehouseBin,
  type WarehouseResponse,
  type WarehouseZone,
} from '@/api/warehouse.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { FormActions } from '@/components/ui/FormActions';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
export function WarehouseSetupPage() {
  const [warehouses, setWarehouses] = useState<WarehouseResponse[]>([]);
  const [selectedId, setSelectedId] = useState<number | ''>('');
  const [zones, setZones] = useState<WarehouseZone[]>([]);
  const [bins, setBins] = useState<WarehouseBin[]>([]);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [warehouseType, setWarehouseType] = useState('CENTRAL');
  const [zoneCode, setZoneCode] = useState('');
  const [zoneName, setZoneName] = useState('');
  const [binCode, setBinCode] = useState('');

  const loadWarehouses = useCallback(() => {
    warehouseApi
      .listWarehouses()
      .then(setWarehouses)
      .catch((e) => showApiError(e, 'Failed to load warehouses'));
  }, []);

  const loadHierarchy = useCallback((id: number) => {
    Promise.all([warehouseApi.listZones(id), warehouseApi.listBins(id)])
      .then(([z, b]) => {
        setZones(z);
        setBins(b);
      })
      .catch((e) => showApiError(e, 'Failed to load zones/bins'));
  }, []);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    if (selectedId) loadHierarchy(selectedId);
    else {
      setZones([]);
      setBins([]);
    }
  }, [selectedId, loadHierarchy]);

  const createWarehouse = async () => {
    if (!code.trim() || !name.trim()) return;
    try {
      await warehouseApi.createWarehouse({ code: code.trim(), name: name.trim(), warehouseType });
      notify.success('Warehouse created');
      setCode('');
      setName('');
      loadWarehouses();
    } catch (e) {
      showApiError(e, 'Create warehouse failed');
    }
  };

  const createZone = async () => {
    if (!selectedId || !zoneCode.trim() || !zoneName.trim()) return;
    try {
      await warehouseApi.createZone(Number(selectedId), {
        zoneCode: zoneCode.trim(),
        zoneName: zoneName.trim(),
      });
      notify.success('Zone created');
      setZoneCode('');
      setZoneName('');
      loadHierarchy(Number(selectedId));
    } catch (e) {
      showApiError(e, 'Create zone failed');
    }
  };

  const createBin = async () => {
    if (!selectedId || !binCode.trim()) return;
    try {
      await warehouseApi.createBin(Number(selectedId), { binCode: binCode.trim(), binType: 'PICKING' });
      notify.success('Bin created');
      setBinCode('');
      loadHierarchy(Number(selectedId));
    } catch (e) {
      showApiError(e, 'Create bin failed');
    }
  };

  return (
    <div className="pb-safe">
      <PageHeader
        title="Warehouse setup"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Warehouse', to: '/warehouse' },
          { label: 'Setup' },
        ]}
      />

      <div className="card mb-6 p-4">
        <h2 className="mb-3 font-semibold">Create warehouse</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} />
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <Select
            label="Type"
            value={warehouseType}
            onChange={(v) => setWarehouseType(v)}
            options={[
              { value: 'CENTRAL', label: 'Central' },
              { value: 'STORE', label: 'Store' },
              { value: 'TRANSIT', label: 'Transit' },
              { value: 'RETURNS', label: 'Returns' },
              { value: 'VIRTUAL', label: 'Virtual' },
            ]}
          />
        </div>
        <Button className="mt-3" onClick={() => void createWarehouse()}>
          Add warehouse
        </Button>
      </div>

      <div className="card mb-6 p-4">
        <Select
          label="Warehouse"
          value={String(selectedId)}
          onChange={(v) => setSelectedId(v ? Number(v) : '')}
          options={[
            { value: '', label: 'Select warehouse…' },
            ...warehouses.map((w) => ({ value: String(w.id), label: `${w.code} — ${w.name}` })),
          ]}
        />
        <ul className="mt-3 text-sm text-slate-600">
          {warehouses.map((w) => (
            <li key={w.id}>
              {w.code} · {w.name} · {w.warehouseType ?? 'CENTRAL'}
            </li>
          ))}
        </ul>
      </div>

      {selectedId && (
        <>
          <div className="card mb-6 p-4">
            <h2 className="mb-3 font-semibold">Zones</h2>
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <Input label="Zone code" value={zoneCode} onChange={(e) => setZoneCode(e.target.value)} />
              <Input label="Zone name" value={zoneName} onChange={(e) => setZoneName(e.target.value)} />
            </div>
            <FormActions
              primaryLabel="Add zone"
              onPrimary={() => void createZone()}
              onReset={() => {
                setZoneCode('');
                setZoneName('');
              }}
              bordered={false}
            />
            <ul className="text-sm">
              {zones.map((z) => (
                <li key={z.id}>
                  {z.zoneCode} — {z.zoneName}
                </li>
              ))}
            </ul>
          </div>
          <div className="card p-4">
            <h2 className="mb-3 font-semibold">Bins</h2>
            <div className="mb-3">
              <Input label="Bin code" value={binCode} onChange={(e) => setBinCode(e.target.value)} />
            </div>
            <FormActions
              primaryLabel="Add bin"
              onPrimary={() => void createBin()}
              onReset={() => setBinCode('')}
              bordered={false}
            />
            <ul className="text-sm">
              {bins.map((b) => (
                <li key={b.id}>
                  {b.binCode} · {b.binType ?? 'PICKING'}
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
